import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { debounce } from "throttle-debounce";
import {
  APISite,
  GetSitesQueryParams,
  UploadFaviconResponse,
} from "../../server/types/index.js";
import { getActiveSite } from "../selectors/sites.js";
import {
  FaviconUploadServerError,
  PatchSiteClient,
  SiteState,
  ThunkAction,
  ThunkDispatch,
} from "../types/index.js";
import { calculatePagination, fetch, isWretchError } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "sites";

const initialState: SiteState = {
  activeSiteId: undefined,
  noMoreItems: false,
  allItems: [],
  byId: {},
};

export const getSites = createAppAsyncThunk(
  `${sliceName}/getSites`,
  async (
    {
      params,
      keepExisting,
    }: { params: GetSitesQueryParams; keepExisting: boolean },
    { dispatch, getState },
  ) => {
    const page = calculatePagination(
      keepExisting,
      getState().sites.allItems.length,
    );

    try {
      const data = await fetch({ dispatch })
        .query({ ...params, page }, { omitUndefinedOrNullValues: true })
        .get("sites")
        .json<APISite[]>();
      return data;
    } catch (error) {
      dispatch(showAlert("Die Website-Liste konnte nicht geladen werden!"));
      throw error;
    }
  },
);

export const getSite = createAppAsyncThunk(
  `${sliceName}/getSite`,
  async (siteId: string, { dispatch }) => {
    const data = await fetch({ dispatch })
      .get(`sites/${siteId}`)
      .json<APISite>();
    return data;
  },
  {
    condition: (_siteId, { getState }) =>
      getState().loadStates.site === "unloaded",
  },
);

// TODO: debounced logic for api calls (in optimistic updates) is not ideal for createAsyncThunk as we can't move the debounce into the ui, hence we keep it as a normal thunk action for now, but it would be good to find a better solution for this in the future
// For example create an optimistic update action that only updates the store and a listener middleware (https://redux-toolkit.js.org/api/createListenerMiddleware) for this action that triggers the debounced api call and handles potential rollback
/**
 * Action for patching the site in the store and through the API
 * @param directSuccess Disabling this doesn’t show a notification
 * and debounces the API request
 */
export const patchSite =
  (siteId: string, siteRequest: PatchSiteClient): ThunkAction<void> =>
  (dispatch, getState) => {
    const previousSite = getState().sites.byId[siteId];
    if (!previousSite) {
      throw new Error(`Previous Site with id ${siteId} not found`);
    }
    dispatch(patchSiteStart({ siteRequest }));

    debouncedPatchSiteRequest({ dispatch, previousSite, siteId, siteRequest });
  };

interface PatchSiteRequestParams {
  dispatch: ThunkDispatch;
  siteId: string;
  siteRequest: PatchSiteClient;
  previousSite: APISite;
}

const patchSiteRequest = ({
  dispatch,
  siteId,
  previousSite,
  siteRequest,
}: PatchSiteRequestParams) =>
  fetch({ dispatch })
    .patch(siteRequest, `sites/${siteId}`)
    .json()
    .catch((error) => {
      dispatch(
        showAlert(
          "Beim Aktualisieren der Seiteneinstellungen ist ein Fehler aufgetreten!",
        ),
      );
      // Reset to previous settings if something went wrong
      dispatch(patchSiteStart({ siteRequest: previousSite }));
      throw error;
    });

const debouncedPatchSiteRequest = debounce(2000, patchSiteRequest);

export const uploadFavicon = createAppAsyncThunk(
  `${sliceName}/uploadFavicon`,
  async ({ siteId, file }: { siteId: string; file: File }, { dispatch }) => {
    try {
      const data = await fetch({ dispatch })
        .headers({ "Content-Type": "image/svg+xml" })
        .post(file, `sites/${siteId}/favicon`)
        .json<UploadFaviconResponse>();
      return data;
    } catch (error) {
      let alertMessage =
        "Beim Hochladen des Favicons ist ein Fehler aufgetreten";
      if (isWretchError<FaviconUploadServerError>(error)) {
        const errorCode = error.json?.data?.code;
        if (errorCode === "file_too_large") {
          alertMessage =
            "Die Datei ist zu groß. Bitte wähle eine kleinere Datei aus.";
        } else if (errorCode === "not_square") {
          alertMessage =
            "Das Favicon muss quadratisch sein. Bitte wähle eine andere Datei aus.";
        } else if (errorCode === "invalid_content_type") {
          alertMessage = "Ungültiger Dateityp. Bitte wähle eine SVG-Datei aus.";
        }
      }
      dispatch(showAlert(alertMessage));
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ siteId }, { getState }) => {
      const site = getState().sites.byId[siteId];
      return !!site;
    },
  },
);

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    patchSiteStart: (
      state,
      {
        payload: { siteRequest },
      }: PayloadAction<{ siteRequest: PatchSiteClient }>,
    ) => {
      const site = getActiveSite(state);
      state.byId[site.id] = { ...site, ...siteRequest };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSites.fulfilled, (state, action) => {
      const sites = action.payload;
      const { keepExisting } = action.meta.arg;
      const allItems = sites.map((site) => site.id);

      sites.forEach((site) => {
        state.byId[site.id] = site;
      });

      state.allItems = keepExisting
        ? [...state.allItems, ...allItems]
        : allItems;
      state.noMoreItems = sites.length !== 10;
    });

    builder.addCase(getSite.fulfilled, (state, action) => {
      const site = action.payload;
      state.allItems = [site.id];
      state.activeSiteId = site.id;
      state.byId[site.id] = site;
    });

    builder.addCase(uploadFavicon.fulfilled, (state, action) => {
      const { url } = action.payload;
      const { siteId } = action.meta.arg;
      if (state.byId[siteId]) {
        state.byId[siteId].favicon.uploadURL = url;
      }
    });
  },
});

export const { patchSiteStart } = slice.actions;

export default slice.reducer;
